import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;1,400;1,600&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

// ── Keyframes
const marqueeLeft = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const marqueeRight = keyframes`
  0%   { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.6; transform: scale(1); }
  50%       { opacity: 1;   transform: scale(1.15); }
`;

const floatY = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-8px); }
`;

// ── Section
const Section = styled.section`
  position: relative;
  background: #220433;
  overflow: hidden;
  padding: 120px 0 140px;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.035;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "120px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
`;

// ── Header
const Header = styled.div`
  text-align: center;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
  margin-bottom: 70px;
  position: relative;
  z-index: 2;
`;

const Eyebrow = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.2rem;
  opacity: 0;
  transform: translateY(16px);
`;

const Title = styled.h2`
  font-family: "Mendl", serif;
  font-size: clamp(2.4rem, 5vw, 4.2rem);
  font-weight: 400;
  line-height: 1.1;
  color: #fff;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(22px);

  em {
    font-style: italic;
    font-weight: 600;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const RatingRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  opacity: 0;
  transform: translateY(14px);
`;

const Stars = styled.div`
  font-size: 1rem;
  letter-spacing: 2px;
  color: #f59e0b;
`;

const RatingNum = styled.div`
  font-family: "Mendl", serif;
  font-size: 1.1rem;
  font-weight: 400;
  color: #fff;
`;

const RatingCount = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.75rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.3);
`;

// ── Marquee rows
const MarqueeSection = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 70px;

  /* Fade edges */
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: clamp(60px, 8vw, 120px);
    z-index: 10;
    pointer-events: none;
  }
  &::before {
    left: 0;
    background: linear-gradient(to right, #050008, transparent);
  }
  &::after {
    right: 0;
    background: linear-gradient(to left, #050008, transparent);
  }
`;

const MarqueeRow = styled.div`
  display: flex;
  width: max-content;
  gap: 16px;
  animation: ${({ reverse }) => (reverse ? marqueeRight : marqueeLeft)}
    ${({ speed }) => speed || "40s"} linear infinite;
  will-change: transform;

  &:hover {
    animation-play-state: paused;
  }
`;

const MiniCard = styled.div`
  flex-shrink: 0;
  width: clamp(260px, 28vw, 340px);
  background: rgb(255, 255, 255);
  border: 1px solid rgba(168, 85, 247, 0.1);
  border-radius: 16px;
  padding: 1.2rem 1.4rem;
  cursor: default;
  transition:
    background 0.25s ease,
    border-color 0.25s ease,
    transform 0.25s ease;

  &:hover {
    background: rgb(226, 219, 233);
    border-color: rgba(168, 85, 247, 0.25);
    transform: translateY(-3px);
  }
`;

const MiniCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const MiniCardUser = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ bg }) => bg || "linear-gradient(135deg, #7c3aed, #a855f7)"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  color: #fff;
  flex-shrink: 0;
`;

const UserInfo = styled.div``;

const UserName = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  color: rgba(75, 72, 72, 0.8);
  line-height: 1;
  margin-bottom: 2px;
`;

const UserMeta = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.62rem;
  font-weight: 300;
  color: rgb(136, 128, 128);
  letter-spacing: 0.04em;
`;

const MiniStars = styled.div`
  font-size: 0.65rem;
  color: #f59e0b;
  letter-spacing: 1px;
`;

const MiniCardText = styled.p`
  font-family: "Mendl", sans-serif;
  font-size: 0.82rem;
  font-weight: 300;
  color: rgb(122, 112, 112);
  line-height: 1.65;
`;

// ── Featured review
const FeaturedWrap = styled.div`
  position: relative;
  z-index: 2;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 clamp(1.5rem, 5vw, 4rem);
  opacity: 0;
  transform: translateY(40px);
`;

const FeaturedCard = styled.div`
  background: rgb(255, 255, 255);
  border: 1px solid rgba(168, 85, 247, 0.18);
  border-radius: 24px;
  padding: clamp(2rem, 4vw, 3.5rem);
  position: relative;
  overflow: hidden;
  animation: ${floatY} 6s ease-in-out infinite;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.5),
      transparent
    );
  }
`;

const FeaturedQuoteMark = styled.div`
  font-family: "Mendl", serif;
  font-size: clamp(5rem, 10vw, 9rem);
  font-style: italic;
  line-height: 0.7;
  color: rgb(81, 75, 87);
  margin-bottom: -1rem;
  pointer-events: none;
`;

const FeaturedText = styled.blockquote`
  font-family: "Mendl", serif;
  font-size: clamp(1.3rem, 2.5vw, 2rem);
  font-style: italic;
  font-weight: 400;
  color: rgba(66, 57, 57, 0.88);
  line-height: 1.55;
  margin-bottom: 2rem;
`;

const FeaturedBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

const FeaturedUser = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FeaturedAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Mendl", sans-serif;
  font-size: 1rem;
  font-weight: 500;
  color: #1d1c1c;
  box-shadow: 0 0 20px rgba(168, 85, 247, 0.3);
`;

const FeaturedUserInfo = styled.div``;

const FeaturedName = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.9rem;
  font-weight: 500;
  color: #7a7575;
  margin-bottom: 2px;
`;

const FeaturedMeta = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.7rem;
  font-weight: 300;
  color: rgba(156, 148, 148, 0.884);
  letter-spacing: 0.06em;
`;

const FeaturedStars = styled.div`
  font-size: 1rem;
  color: #f59e0b;
  letter-spacing: 2px;
`;

// Verified badge
const VerifiedBadge = styled.div`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(74, 222, 128, 0.1);
  border: 1px solid rgba(74, 222, 128, 0.25);
  border-radius: 100px;
  padding: 4px 10px;

  span {
    font-family: "Mendl", sans-serif;
    font-size: 0.6rem;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #4ade80;
  }

  div {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: #4ade80;
    animation: ${pulse} 2s ease-in-out infinite;
  }
`;

// ── App store strip
const StoreStrip = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2.5rem;
  margin-top: 60px;
  flex-wrap: wrap;
  opacity: 0;
  transform: translateY(20px);
`;

const StoreStat = styled.div`
  text-align: center;
`;

const StoreStatNum = styled.div`
  font-family: "Mendl", serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 400;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.3rem;

  span {
    color: #c084fc;
  }
`;

const StoreStatLabel = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
`;

const StoreStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(168, 85, 247, 0.2);
`;

// ── Review data
const REVIEWS_ROW1 = [
  {
    name: "Jade M.",
    meta: "London · 24",
    stars: 5,
    initials: "JM",
    bg: "linear-gradient(135deg,#7c3aed,#a855f7)",
    text: "Finally an app that doesn't feel like a LinkedIn profile. I matched with someone in 10 minutes and we met that weekend.",
  },
  {
    name: "Alex R.",
    meta: "NYC · 27",
    stars: 5,
    initials: "AR",
    bg: "linear-gradient(135deg,#e879f9,#a855f7)",
    text: "The disappearing photos feature is actually genius. Feels way safer than anything else I've used.",
  },
  {
    name: "Priya S.",
    meta: "Toronto · 23",
    stars: 5,
    initials: "PS",
    bg: "linear-gradient(135deg,#c026d3,#7c3aed)",
    text: "As a woman I was skeptical but the red flag system is real. Got warned about someone and it saved me a dodgy situation.",
  },
  {
    name: "Marcus D.",
    meta: "LA · 29",
    stars: 4,
    initials: "MD",
    bg: "linear-gradient(135deg,#a855f7,#6d28d9)",
    text: "The count system is brutal in the best way. Total transparency. You know exactly who you're dealing with.",
  },
  {
    name: "Sophie L.",
    meta: "Paris · 25",
    stars: 5,
    initials: "SL",
    bg: "linear-gradient(135deg,#e879f9,#db2777)",
    text: "I love how unapologetically physical-attraction-first this is. No pretending we're here for personality quizzes.",
  },
  {
    name: "Remy T.",
    meta: "Amsterdam · 26",
    stars: 5,
    initials: "RT",
    bg: "linear-gradient(135deg,#7c3aed,#4f46e5)",
    text: "Matched, met, clicked. Whole thing took 3 days. This app just gets out of the way and lets it happen.",
  },
];

const REVIEWS_ROW2 = [
  {
    name: "Kai W.",
    meta: "Sydney · 22",
    stars: 5,
    initials: "KW",
    bg: "linear-gradient(135deg,#a855f7,#e879f9)",
    text: "V-Card badge is hilarious but kind of iconic. The whole thing feels gamified in a fun, not manipulative way.",
  },
  {
    name: "Isabella F.",
    meta: "Milan · 28",
    stars: 5,
    initials: "IF",
    bg: "linear-gradient(135deg,#6d28d9,#a855f7)",
    text: "Built by a woman who actually understands safety concerns. You can feel that in every design decision.",
  },
  {
    name: "Noah B.",
    meta: "Berlin · 31",
    stars: 4,
    initials: "NB",
    bg: "linear-gradient(135deg,#4f46e5,#7c3aed)",
    text: "No algorithm trying to decide who I should like. Just people. Real photos. I decide. Refreshing.",
  },
  {
    name: "Zara A.",
    meta: "Dubai · 24",
    stars: 5,
    initials: "ZA",
    bg: "linear-gradient(135deg,#db2777,#a855f7)",
    text: "The security features are next level. My friend got someone suspended within an hour of reporting them.",
  },
  {
    name: "Liam K.",
    meta: "Dublin · 26",
    stars: 5,
    initials: "LK",
    bg: "linear-gradient(135deg,#7c3aed,#c026d3)",
    text: "Every other dating app feels like a chore after this. It's just honest about what it is and that's everything.",
  },
  {
    name: "Mia C.",
    meta: "Singapore · 23",
    stars: 5,
    initials: "MC",
    bg: "linear-gradient(135deg,#a855f7,#e879f9)",
    text: "Matched with someone genuine on day one. The vibe of this app attracts people who are actually direct and real.",
  },
];

const FEATURED = {
  text: "I've tried every dating app out there and nothing comes close to how NudR makes me feel. Safe enough to be bold. That combination shouldn't be rare — but it is. And NudR actually pulled it off.",
  name: "Chloe R.",
  meta: "New York · Verified User",
  initials: "CR",
};

function StarStr(n) {
  return "★".repeat(n) + "☆".repeat(5 - n);
}

export default function ReviewsSection() {
  const sectionRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const ratingRef = useRef(null);
  const featuredRef = useRef(null);
  const stripRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header
      gsap
        .timeline({
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 78%",
            end: "top 44%",
            scrub: 0.7,
          },
        })
        .to(eyebrowRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0)
        .to(titleRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.1)
        .to(ratingRef.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.2);

      // Featured
      gsap
        .timeline({
          scrollTrigger: {
            trigger: featuredRef.current,
            start: "top 82%",
            end: "top 48%",
            scrub: 0.8,
          },
        })
        .to(featuredRef.current, { opacity: 1, y: 0, ease: "power3.out" });

      // Strip
      gsap
        .timeline({
          scrollTrigger: {
            trigger: stripRef.current,
            start: "top 88%",
            end: "top 60%",
            scrub: 0.7,
          },
        })
        .to(stripRef.current, { opacity: 1, y: 0, ease: "power2.out" });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Section ref={sectionRef} id="reviews">
        <Grain />
        <Orb
          s="500px"
          c="rgba(185, 163, 224, 0.2)"
          top="-5%"
          left="-8%"
          op={0.2}
          blur="130px"
        />
        <Orb
          s="400px"
          c="rgba(232,121,249,0.15)"
          bottom="5%"
          right="-5%"
          op={0.18}
          blur="110px"
        />

        {/* Header */}
        <Header>
          <Eyebrow ref={eyebrowRef}>What people are saying</Eyebrow>
          <Title ref={titleRef}>
            Real people.
            <br />
            <em>Real reactions.</em>
          </Title>
          <RatingRow ref={ratingRef}>
            <Stars>★★★★★</Stars>
            <RatingNum>4.9</RatingNum>
            <RatingCount>· 2,400+ reviews on the App Store</RatingCount>
          </RatingRow>
        </Header>

        {/* Marquee rows */}
        <MarqueeSection>
          {/* Row 1 — left */}
          <MarqueeRow speed="45s">
            {[...REVIEWS_ROW1, ...REVIEWS_ROW1].map((r, i) => (
              <MiniCard key={i}>
                <MiniCardTop>
                  <MiniCardUser>
                    <Avatar bg={r.bg}>{r.initials}</Avatar>
                    <UserInfo>
                      <UserName>{r.name}</UserName>
                      <UserMeta>{r.meta}</UserMeta>
                    </UserInfo>
                  </MiniCardUser>
                  <MiniStars>{StarStr(r.stars)}</MiniStars>
                </MiniCardTop>
                <MiniCardText>{r.text}</MiniCardText>
              </MiniCard>
            ))}
          </MarqueeRow>

          {/* Row 2 — right */}
          <MarqueeRow speed="38s" reverse>
            {[...REVIEWS_ROW2, ...REVIEWS_ROW2].map((r, i) => (
              <MiniCard key={i}>
                <MiniCardTop>
                  <MiniCardUser>
                    <Avatar bg={r.bg}>{r.initials}</Avatar>
                    <UserInfo>
                      <UserName>{r.name}</UserName>
                      <UserMeta>{r.meta}</UserMeta>
                    </UserInfo>
                  </MiniCardUser>
                  <MiniStars>{StarStr(r.stars)}</MiniStars>
                </MiniCardTop>
                <MiniCardText>{r.text}</MiniCardText>
              </MiniCard>
            ))}
          </MarqueeRow>
        </MarqueeSection>

        {/* Featured review */}
        <FeaturedWrap ref={featuredRef}>
          <FeaturedCard>
            <VerifiedBadge>
              <div />
              <span>Verified user</span>
            </VerifiedBadge>
            <FeaturedQuoteMark>"</FeaturedQuoteMark>
            <FeaturedText>{FEATURED.text}</FeaturedText>
            <FeaturedBottom>
              <FeaturedUser>
                <FeaturedAvatar>{FEATURED.initials}</FeaturedAvatar>
                <FeaturedUserInfo>
                  <FeaturedName>{FEATURED.name}</FeaturedName>
                  <FeaturedMeta>{FEATURED.meta}</FeaturedMeta>
                </FeaturedUserInfo>
              </FeaturedUser>
              <FeaturedStars>★★★★★</FeaturedStars>
            </FeaturedBottom>
          </FeaturedCard>
        </FeaturedWrap>

        {/* Store stats strip */}
        <StoreStrip ref={stripRef}>
          <StoreStat>
            <StoreStatNum>
              4.9<span>★</span>
            </StoreStatNum>
            <StoreStatLabel>App Store rating</StoreStatLabel>
          </StoreStat>
          <StoreStatDivider />
          <StoreStat>
            <StoreStatNum>
              2.4<span>k+</span>
            </StoreStatNum>
            <StoreStatLabel>Reviews</StoreStatLabel>
          </StoreStat>
          <StoreStatDivider />
          <StoreStat>
            <StoreStatNum>
              89<span>%</span>
            </StoreStatNum>
            <StoreStatLabel>5-star ratings</StoreStatLabel>
          </StoreStat>
          <StoreStatDivider />
          <StoreStat>
            <StoreStatNum>
              67<span>%</span>
            </StoreStatNum>
            <StoreStatLabel>Meet within a week</StoreStatLabel>
          </StoreStat>
        </StoreStrip>
      </Section>
    </>
  );
}
